import {Inject, Injectable, Injector, runInInjectionContext, signal, inject} from '@angular/core';
import {ActivatedRoute, NavigationEnd, NavigationStart, Router, UrlTree} from "@angular/router";
import {DOCUMENT} from "@angular/common";
import {App} from "@capacitor/app";
import {PluginListenerHandle, registerPlugin} from "@capacitor/core";
import {getAllRouteData, getDeepestChildRoute, platform} from "../utils";
import {assert} from "typia";
import {filter} from "rxjs";
import {BackNavigationService, isLeftOutOfBackHistory} from "./back-navigation.service";

const tabRoutes = ['/applications', '/support', '/profile', '/lotteries'];

// Open PrimeNG overlays. Each closes on Escape (the fullscreen galleria through its host component).
// Dialogs close only when on top and closable; the drawer and popover close on any Escape.
const overlaySelector = '.p-select-overlay, .p-datepicker-panel:not(.p-datepicker-panel-inline), .p-popover, '
  + '.p-dialog, .p-drawer, .p-galleria-mask';
// A date picker or select whose popup is open. Each handles Escape only on its own input, and focus
// may have left it (a tap on a calendar cell moves it to the body).
const openComboboxSelector = '[role="combobox"][aria-expanded="true"]';

// The iOS edge swipe (ios/App/App/SwipeBackPlugin.swift). It slides a snapshot of the page over
// the one `capture()` took of the back target, and sends `back` when the swipe commits; `finish()`
// says the previous page has painted. A null back target turns the swipe off.
const SwipeBack = registerPlugin<{
  setBackTarget(options: {url: string | null}): Promise<void>;
  capture(options: {url: string}): Promise<void>;
  finish(): Promise<void>;
  addListener(eventName: 'back', listener: () => void): Promise<PluginListenerHandle>;
}>('SwipeBack');

@Injectable()
export class BrowserBackNavigationService extends BackNavigationService {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly injector = inject(Injector);
  private readonly storageKey = 'navigationHistory';
  private history: string[] = [];
  private readonly method = signal<'forward' | 'backward'>('forward');
  private readonly _lastNavigationWasBack = signal(false);
  // Whether the navigation in flight was asked to replace its history entry (set at its start).
  private replacing = false;
  public readonly lastNavigationWasBack = this._lastNavigationWasBack.asReadonly();

  private pathOf(url: string): string {
    const tree: UrlTree = this.router.parseUrl(url);
    const primary = tree.root.children['primary'];
    return primary ? '/' + primary.segments.map(s => s.path).join('/') : '/';
  }

  constructor(@Inject(DOCUMENT) private readonly doc: Document) {
    super();
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationStart),
      )
      .subscribe((ev) => {
        const isPop = ev.navigationTrigger === 'popstate' || this.method() === 'backward';
        const isBack = isPop;
        this._lastNavigationWasBack.set(isBack);
        // Angular marks its own popstate navigations replaceUrl too, and the initial navigation of a
        // full page load (a typed URL, a bookmark, a Stripe or OAuth return), which must not drop the
        // page restored before it: only the code's own requests count. navigateBack's own navigation
        // counts too: going back to a backRoute replaces the page left. A `leaveOutOfBackHistory` state
        // replaces here only, keeping the browser's entry.
        const extras = this.router.currentNavigation()?.extras;
        this.replacing = ev.navigationTrigger === 'imperative' && this.router.navigated
          && (extras?.replaceUrl === true || isLeftOutOfBackHistory(extras?.state));

        this.method.set("forward");
        const toPath = this.pathOf(ev.url);
        const samePath = toPath === this.pathOf(this.router.url);
        // The page as it looks when the user leaves it, so a swipe back to it can show it behind the finger.
        if (platform === 'ios' && !isBack && !samePath) {
          SwipeBack.capture({url: this.router.url}).catch(e => console.error('Failed to capture the page', e));
        }
        // The swipe already slid the page away. A popstate restores the marker from the history
        // entry, so it counts only on the swipe's own navigation.
        const swipeBack = ev.navigationTrigger !== 'popstate'
          && extras?.state?.['swipeBack'] === true;
        if (swipeBack || (tabRoutes.includes(toPath) && !isPop) || samePath) {
          this.doc.documentElement.classList.remove('is-back', 'is-forward');
          return;
        }

        this.doc.documentElement.classList.toggle('is-back', isBack);
        this.doc.documentElement.classList.toggle('is-forward', !isBack);
      });

    const storedHistory = sessionStorage.getItem(this.storageKey);
    if (storedHistory) {
      try {
        this.history = assert<string[]>(JSON.parse(storedHistory));
      } catch (e) {
        console.error('Error parsing stored navigation history', e);
        this.history = [];
      }
    }

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const url = event.urlAfterRedirects;
        if (this.history.length > 0 && this.history[this.history.length - 1] === url) {
          return;
        }

        // A replaceUrl navigation replaces the page it leaves, as in the browser's history, so Back
        // skips a finished step (a used reset code, a payment success page) instead of returning to it.
        if (this.replacing) {
          this.history.pop();
        }
        if (this.history.at(-1) !== url) {
          this.history.push(url);
        }
        sessionStorage.setItem(this.storageKey, JSON.stringify(this.history));
      });
  }

  private popHistory(): void {
    if (this.history.length > 1) {
      this.history.pop();
      sessionStorage.setItem(this.storageKey, JSON.stringify(this.history));
    }
  }

  public initialize() {
    // On Android, without a listener Capacitor falls back to WebView history, which the arrow's
    // replaceUrl navigations never shrink.
    if (platform === 'android') {
      void App.addListener('backButton', () => this.onHardwareBack());
    } else if (platform === 'ios') {
      this.initializeSwipeBack();
    }
  }

  // The swipe works where the header shows the back arrow and no overlay is open, so it never
  // leaves a page the arrow can't, and never navigates under a drawer or dialog.
  private initializeSwipeBack(): void {
    // Unknown at first: the native target outlives a web view reload, so the first update always sends.
    let backTarget: string | null | undefined;
    let scheduled = false;
    const update = () => {
      scheduled = false;
      const url = this.hasBackArrow() && !this.hasOpenOverlay() ? this.backTarget().url : null;
      if (url !== backTarget) {
        backTarget = url;
        SwipeBack.setBackTarget({url}).catch(e => console.error('Failed to update swipe back', e));
      }
    };
    const schedule = () => {
      if (!scheduled) {
        scheduled = true;
        requestAnimationFrame(update);
      }
    };
    new MutationObserver(schedule).observe(this.doc.body, {childList: true, subtree: true});
    schedule();

    void SwipeBack.addListener('back', () => void this.onSwipeBack());
  }

  private async onSwipeBack(): Promise<void> {
    await this.navigateBack(true).catch(e => console.error('Swipe back navigation failed', e));
    // Two frames: the first runs before the new page paints.
    await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    SwipeBack.finish().catch(e => console.error('Failed to finish swipe back', e));
  }

  // Closes the top overlay first, and never navigates while one is open. Otherwise it does what
  // the header's back arrow does, backgrounds the app on a start screen, and stays on a page that
  // shows no arrow.
  private onHardwareBack(): void {
    if (this.closeTopOverlay()) {
      return;
    }

    const path = this.pathOf(this.router.url);
    if (path === '/' || tabRoutes.includes(path)) {
      App.minimizeApp().catch(e => console.error('Failed to background the app', e));
      return;
    }

    if (this.hasBackArrow()) {
      this.navigateBack();
    }
  }

  // The header renders the arrow only on pages that have one.
  private hasBackArrow(): boolean {
    return this.doc.querySelector('homey-back-button') !== null;
  }

  private hasOpenOverlay(): boolean {
    return this.doc.querySelector(overlaySelector) !== null;
  }

  private closeTopOverlay(): boolean {
    if (!this.hasOpenOverlay()) {
      return false;
    }
    // Popovers, dialogs and drawers listen on the document; each overlay checks a different field.
    const target = this.doc.querySelector(openComboboxSelector) ?? this.doc.activeElement ?? this.doc.body;
    target.dispatchEvent(new KeyboardEvent('keydown', {
      key: 'Escape', code: 'Escape', keyCode: 27, which: 27, bubbles: true, cancelable: true
    }));
    return true;
  }

  private resolveBackRoute(): string[] | undefined {
    const backRouteValue = getAllRouteData(this.activatedRoute).backRoute;
    return typeof backRouteValue === 'function'
      ? runInInjectionContext(this.injector, backRouteValue)
      : backRouteValue;
  }

  // Where navigateBack() goes: the route's backRoute, else the previous page in history.
  private backTarget(): {readonly url: string; readonly fromHistory: boolean} {
    const resolvedBackRoute = this.resolveBackRoute();
    if (resolvedBackRoute) {
      const url = this.router.serializeUrl(this.router.createUrlTree(resolvedBackRoute, {
        relativeTo: getDeepestChildRoute(this.activatedRoute)
      }));
      return {url, fromHistory: false};
    }
    const url = this.history.length > 1 ? this.history[this.history.length - 2] : '/';
    return {url, fromHistory: true};
  }

  public navigateBack(swipeBack = false): Promise<boolean> {
    this.method.set("backward");
    const target = this.backTarget();
    if (target.fromHistory) {
      this.popHistory();
    }
    return this.router.navigateByUrl(target.url, {
      replaceUrl: true, state: {backNavigation: true, swipeBack}
    });
  }
}
